import { AccordionLabel } from "@/components/AccordionLabel/AccordionLabel";
import { CheckoutBillingForm } from "@/components/CheckoutForm/CheckoutBillingForm";
import { CheckoutPaymentForm } from "@/components/CheckoutForm/CheckoutPaymentForm";
import { CheckoutReviewOrder } from "@/components/CheckoutForm/CheckoutReviewOrder";
import { CheckoutShippingForm } from "@/components/CheckoutForm/CheckoutShippingForm";
import { CheckoutContext } from "@/components/CheckoutForm/utils";
import { TRIBECA_ADDRESS } from "@/utils/constants";
import { usePending } from "@/utils/hooks/usePending.js";
import { Accordion, Box } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import React, { useState } from "react";

type CheckoutFormProps = {
  ctx: CheckoutContext;
};

const checkoutPanels = [
  {
    id: "1",
    label: "Billing Details",
    component: CheckoutBillingForm,
  },
  {
    id: "2",
    label: "Shipping Information",
    component: CheckoutShippingForm,
  },
  {
    id: "3",
    label: "Review Order",
    component: CheckoutReviewOrder,
  },
  {
    id: "4",
    label: "Payment Method",
    component: CheckoutPaymentForm,
  },
];

export function CheckoutForm({ ctx }: CheckoutFormProps) {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const pendingNextStep =
    typeof window !== "undefined"
      ? Number(sessionStorage.getItem("checkout_pending_next_step") || "0")
      : 0;
  // FIX 2026-08-05 (AU customer, "totals are still syncing" dead end):
  // every page load mints a FRESH, unfinalized session (the loader's POST
  // /checkout), but these sessionStorage restores could park a returning
  // customer straight on Payment — where Pay can only fail, because
  // nothing re-finalizes without Review's Save & Continue. If the session
  // isn't finalized, cap the restored step at Review Order (step 3). The
  // post-finalize remount is unaffected: its session IS finalized, so the
  // clamp is a no-op there.
  const clampStepForUnfinalizedSession = (step: number) =>
    ctx.isSessionFinalized ? step : Math.min(step, 3);
  const getInitialActiveStep = () =>
    String(
      clampStepForUnfinalizedSession(
        pendingNextStep > 0
          ? pendingNextStep
          : Number(
              (typeof window !== "undefined"
                ? sessionStorage.getItem("checkout_active_step")
                : null) || "1"
            ) || 1
      )
    );
  const getInitialMaxPassedStep = () =>
    clampStepForUnfinalizedSession(
      pendingNextStep > 0
        ? Math.max(
            pendingNextStep,
            typeof window !== "undefined"
              ? Number(
                  sessionStorage.getItem("checkout_max_passed_step") || "1"
                )
              : 1
          )
        : typeof window !== "undefined"
          ? Number(sessionStorage.getItem("checkout_max_passed_step") || "1")
          : 1
    );
  const getInitialCollapsePanel = () =>
    typeof window !== "undefined"
      ? sessionStorage.getItem("checkout_collapse_panel") === "true"
      : false;

  const [activeStep, setActiveStep] = useState(getInitialActiveStep);
  const [maxPassedStep, setMaxPassedStep] = useState(getInitialMaxPassedStep);
  const [collapsePanel, setCollapsePanel] = useState(getInitialCollapsePanel);

  // Update context with current active step and collapse panel state
  React.useEffect(() => {
    if (ctx.setActiveStep) {
      ctx.setActiveStep(activeStep);
    }
  }, [activeStep, ctx]);

  React.useEffect(() => {
    if (ctx.setCollapsePanel) {
      ctx.setCollapsePanel(collapsePanel);
    }
  }, [collapsePanel, ctx]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (pendingNextStep > 0) {
      sessionStorage.removeItem("checkout_pending_next_step");
    }
  }, [pendingNextStep]);

  // FIX 2026-08-27 (T-50997 follow-up): a cart edit revalidates the route and
  // mints a fresh, UNFINALIZED session, which forces reviewCompleted to false
  // (utils.ts getInitialReviewCompleted). CONFIRM AND PAY renders at zero
  // height while that flag is false, and the accordion renders
  // `value={collapsePanel ? null : activeStep}` — so a customer who had
  // finished Review (panels collapsed) was left with a corrected total, every
  // panel shut, and no button. Measured on staging twice.
  //
  // Two earlier attempts keyed on the cart signature CHANGING; both missed,
  // because the change straddles a remount. The dead end is a STATE, not an
  // event: collapsed + review invalidated. Read it on every render instead.
  //
  // hadCompletedReview is captured in a ref initializer, which runs during the
  // first render — before the effect in useCheckoutContext that rewrites
  // checkout_review_completed to match the fresh (false) flag. Without that
  // ordering the evidence is gone before this can read it.
  //
  // Manual panel closes are unaffected: closing a panel never clears
  // reviewCompleted, so a customer who simply collapsed the accordion has
  // reviewCompleted true and this does not fire.
  const hadCompletedReviewRef = React.useRef(
    typeof window !== "undefined" &&
      sessionStorage.getItem("checkout_review_completed") === "true"
  );
  React.useEffect(() => {
    if (!hadCompletedReviewRef.current) return;
    if (!collapsePanel || ctx.reviewCompleted) return;
    hadCompletedReviewRef.current = false;
    setCollapsePanel(false);
    setActiveStep("3");
  }, [collapsePanel, ctx.reviewCompleted]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    sessionStorage.setItem("checkout_active_step", activeStep);
    sessionStorage.setItem("checkout_max_passed_step", String(maxPassedStep));
    sessionStorage.setItem(
      "checkout_collapse_panel",
      collapsePanel ? "true" : "false"
    );
  }, [activeStep, maxPassedStep, collapsePanel]);

  const confirmPayment = usePending(async function () {
    try {
      if (!ctx.isSessionFinalized) {
        throw new Error(
          "Checkout totals are still syncing. Please wait a moment and try again."
        );
      }
      if (ctx.isFinalizingSession) {
        throw new Error(
          "Checkout session is finalizing. Please wait a moment and try again."
        );
      }
      if (
        ctx.data.shippingType === "pickup" ||
        ctx.data.shippingType === "oversized"
      ) {
        const shippingOptionId =
          ctx.data.shippingType === "oversized" &&
          ctx.shippingOversizedOption?.value
            ? ctx.shippingOversizedOption.value
            : ctx.shippingPickupOption.value;
        await Promise.all([
          ctx.checkout.updateShippingAddress({
            name:
              ((ctx.data.billingAddress as any)?.name as string) ||
              `${ctx.data.firstName} ${ctx.data.lastName}`,
            address: TRIBECA_ADDRESS.address,
          }),
          ctx.checkout.updateShippingOption(shippingOptionId),
        ]);
      }
      const res = await ctx.confirm();

      if (res.type === "error")
        throw new Error("Failed to confirm payment. Try again later.");
    } catch (error) {
      alert("Failed to confirm payment. Try again later.");
    }
  });

  const finalizeReview = usePending(async function () {
    return ctx.finalizeReviewSession();
  });

  const handleNext = async (currentStep: string) => {
    const currentIndex = parseInt(currentStep);
    const nextMaxPassedStep = Math.max(maxPassedStep, currentIndex + 1);
    // FIX 2026-08-05: don't unlock Payment before Review's finalize has
    // actually succeeded — the bump for step 3 happens below, on success.
    if (currentIndex !== 3) {
      setMaxPassedStep(nextMaxPassedStep);
    }

    if (currentIndex === 3) {
      if (typeof window !== "undefined") {
        sessionStorage.setItem("checkout_pending_next_step", "4");
      }

      const finalizeResult = await finalizeReview();
      if (finalizeResult?.type === "session_replaced") {
        return;
      }

      if (typeof window !== "undefined") {
        sessionStorage.removeItem("checkout_pending_next_step");
      }
      // FIX 2026-08-05: only advance to Payment when the session actually
      // finalized. finalizeReviewSession returns undefined when it bails
      // early (incomplete shipping destination) and "already_finalizing"
      // when another call is in flight — advancing on those parked the
      // customer on a Pay button that could only throw "totals are still
      // syncing" with no way to satisfy it.
      if (
        finalizeResult?.type !== "finalized" &&
        finalizeResult?.type !== "already_finalized"
      ) {
        return;
      }
      setMaxPassedStep(prev => Math.max(prev, 4));
      setActiveStep("4");
      setCollapsePanel(false);
      ctx.setReviewCompleted(false);
      return;
    }

    if (currentIndex === checkoutPanels.length) {
      setCollapsePanel(true);
      ctx.setReviewCompleted(true);
      return;
    }

    if (currentIndex < checkoutPanels.length) {
      setActiveStep(`${currentIndex + 1}`);
      setCollapsePanel(false);
      ctx.setReviewCompleted(false);
    }
  };

  const handlePrev = (currentStep: string) => {
    const currentIndex = parseInt(currentStep);
    if (currentIndex > 1) {
      setActiveStep(`${currentIndex - 1}`);
      setCollapsePanel(false);
      ctx.setReviewCompleted(false);
    }
  };

  const getPanelTitle = (panelId: string) => {
    switch (panelId) {
      case "1":
        return ctx.data.firstName && ctx.data.lastName
          ? `${ctx.data.firstName} ${ctx.data.lastName}${ctx.data.email ? ` • ${ctx.data.email}` : ""}`
          : "Add Billing Address and Payment Method";
      case "2":
        return ctx.data.shippingType
          ? `${ctx.data.shippingType === "pickup" ? "Pickup" : "Shipping"} Selected`
          : "Add delivery address";
      case "3":
        return "Double-check your order details";
      case "4":
        return "Add payment method";
      default:
        return checkoutPanels.find(p => p.id === panelId)?.label || "";
    }
  };

  const titles = Object.fromEntries(
    checkoutPanels.map(panel => [panel.id, getPanelTitle(panel.id)])
  );

  return (
    <div className="h-full md:px-6 md:pb-4 md:pt-0">
      <Accordion
        radius={isMobile ? 0 : undefined}
        transitionDuration={600}
        chevronPosition="right"
        variant="contained"
        p={0}
        value={collapsePanel ? null : activeStep}
        pos="relative"
        onChange={value => {
          if (value === null) {
            // Panel is being closed
            setCollapsePanel(true);
          } else if (maxPassedStep >= parseInt(value)) {
            // Panel is being opened
            setCollapsePanel(false);
            setActiveStep(value);
          }
        }}
      >
        {checkoutPanels.map(panel => {
          return (
            <Accordion.Item
              value={panel.id}
              key={panel.id}
              bg="white"
              pos="relative"
            >
              <Accordion.Control
                classNames={{
                  control: `sticky top-0 z-30 !py-3 md:!py-4`,
                }}
                style={{
                  pointerEvents:
                    maxPassedStep >= parseInt(panel.id) ? "auto" : "none",
                }}
              >
                <Box>
                  <AccordionLabel
                    color="giclee"
                    id={panel.id}
                    label={panel.label}
                    activePanel={activeStep}
                    activeSelections={titles}
                    maxPanel={checkoutPanels.length}
                    showSelections={false}
                  />
                </Box>
              </Accordion.Control>
              <Accordion.Panel>
                <div
                  style={{
                    maxHeight: "unset",
                  }}
                  className={`relative overflow-scroll`}
                >
                  <panel.component
                    ctx={ctx}
                    onNext={() => {
                      void handleNext(panel.id);
                    }}
                    onPrev={() => handlePrev(panel.id)}
                    nextIsPending={
                      panel.id === "3"
                        ? finalizeReview.isPending || ctx.isFinalizingSession
                        : confirmPayment.isPending
                    }
                    isPanelOpen={!collapsePanel && activeStep === panel.id}
                  />
                </div>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </div>
  );
}
